import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface ExistingImage {
    id?: string;
    url: string;
}

interface PreviewImage {
    file: File;
    previewUrl: string;
}

interface ImageUploaderProps {
    existingImages?: ExistingImage[];
    onFilesChange: (files: File[]) => void;
    onDeleteExisting: (url: string) => void;
    maxImages?: number;
}

// Convert file to base64 data URL
const fileToDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
    existingImages = [],
    onFilesChange,
    onDeleteExisting,
    maxImages = 10,
}) => {
    const { t } = useLanguage();
    const [newImages, setNewImages] = useState<PreviewImage[]>([]);
    const [deletedExisting, setDeletedExisting] = useState<Set<string>>(new Set());
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Filter out deleted existing images
    const visibleExisting = existingImages.filter(img => !deletedExisting.has(img.url));

    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const files = Array.from(e.target.files);
        const totalImages = visibleExisting.length + newImages.length + files.length;

        if (totalImages > maxImages) {
            alert(t('validation.max_images_exceeded') || `You can only upload up to ${maxImages} images.`);
            return;
        }

        // Create preview images with base64 data URLs
        const newPreviews: PreviewImage[] = [];
        for (const file of files) {
            try {
                const dataUrl = await fileToDataUrl(file);
                newPreviews.push({
                    file,
                    previewUrl: dataUrl
                });
            } catch (err) {
                console.error('Failed to read file:', file.name, err);
            }
        }

        const updatedNewImages = [...newImages, ...newPreviews];
        setNewImages(updatedNewImages);
        onFilesChange(updatedNewImages.map(img => img.file));

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [visibleExisting.length, newImages, maxImages, t, onFilesChange]);

    const removeNewImage = useCallback((index: number) => {
        const updatedImages = newImages.filter((_, i) => i !== index);
        setNewImages(updatedImages);
        onFilesChange(updatedImages.map(img => img.file));
    }, [newImages, onFilesChange]);

    const handleDeleteExisting = useCallback((url: string) => {
        setDeletedExisting(prev => new Set(prev).add(url));
        onDeleteExisting(url);
    }, [onDeleteExisting]);

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {/* Existing Images */}
                {visibleExisting.map((img, index) => (
                    <div key={`existing-${img.id || index}`} className="relative group aspect-video bg-gray-800 rounded-lg overflow-hidden border border-gray-600">
                        <img
                            src={img.url}
                            alt={`Görsel ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                    const errorDiv = document.createElement('div');
                                    errorDiv.className = 'w-full h-full flex items-center justify-center text-gray-500 text-xs';
                                    errorDiv.textContent = 'Yüklenemedi';
                                    parent.appendChild(errorDiv);
                                }
                            }}
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                                type="button"
                                onClick={() => handleDeleteExisting(img.url)}
                                className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-full transition-colors"
                                title={t('common.delete') || 'Sil'}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                            Mevcut
                        </span>
                    </div>
                ))}

                {/* New Images (with blue ring) */}
                {newImages.map((img, index) => (
                    <div key={`new-${index}`} className="relative group aspect-video bg-gray-800 rounded-lg overflow-hidden ring-2 ring-blue-500">
                        <img
                            src={img.previewUrl}
                            alt={`Yeni Görsel ${index + 1}`}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                                type="button"
                                onClick={() => removeNewImage(index)}
                                className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-full transition-colors"
                                title={t('common.delete') || 'Sil'}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                        <span className="absolute bottom-1 left-1 bg-blue-600 text-white text-xs px-1 rounded">
                            Yeni
                        </span>
                    </div>
                ))}

                {/* Upload Button */}
                {(visibleExisting.length + newImages.length) < maxImages && (
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-video flex flex-col items-center justify-center border-2 border-dashed border-gray-600 rounded-lg hover:border-blue-500 hover:bg-gray-800/50 transition-colors text-gray-400 hover:text-blue-400"
                    >
                        <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-xs">Görsel Ekle</span>
                    </button>
                )}
            </div>

            <p className="text-xs text-gray-500">
                {visibleExisting.length + newImages.length} / {maxImages} görsel
            </p>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept="image/*"
                className="hidden"
            />
        </div>
    );
};

export default ImageUploader;
